import { t } from "../lib/i18n";
/**
 * Definitions for the 10-stage seller process.
 * Each stage has a list of fields with type and label.
 */
export const LAST_CLOSED_YEAR = new Date().getFullYear() - 1;
// [hace 2 ejercicios, ejercicio anterior, último ejercicio cerrado]
export const CLOSED_YEARS = [LAST_CLOSED_YEAR - 2, LAST_CLOSED_YEAR - 1, LAST_CLOSED_YEAR];

export const SELLER_STAGES = [
  {
    id: 1,
    name: t("Información Básica"),
    desc: t("Datos generales sobre la empresa para empezar."),
    fields: [
      { key: "year_founded", label: t("Año de fundación"), type: "number" },
      { key: "employees", label: t("Número de empleados"), type: "number" },
      { key: "ownership", label: t("Estructura accionarial"), type: "text", placeholder: t("p. ej. 100% familiar") },
      { key: "website", label: t("Sitio web"), type: "text", placeholder: t("https://") },
      { key: "short_description", label: t("Descripción breve (1 línea)"), type: "textarea" },
    ],
  },
  {
    id: 2,
    name: t("Financieros"),
    desc: `Ingresos y EBITDA de los 3 últimos ejercicios cerrados (${CLOSED_YEARS[0]}-${CLOSED_YEARS[2]}) y cifras clave.`,
    fields: [
      { key: "revenue_y1_eur", label: `Ingresos ${CLOSED_YEARS[0]} (€)`, type: "number" },
      { key: "revenue_y2_eur", label: `Ingresos ${CLOSED_YEARS[1]} (€)`, type: "number" },
      { key: "revenue_eur", label: `Ingresos ${CLOSED_YEARS[2]} · último ejercicio (€)`, type: "number" },
      { key: "ebitda_y1_eur", label: `EBITDA ${CLOSED_YEARS[0]} (€)`, type: "number" },
      { key: "ebitda_y2_eur", label: `EBITDA ${CLOSED_YEARS[1]} (€)`, type: "number" },
      { key: "ebitda_eur", label: `EBITDA ${CLOSED_YEARS[2]} · último ejercicio (€)`, type: "number" },
      { key: "ebitda_margin_pct", label: t("Margen EBITDA (%)"), type: "number" },
      { key: "growth_pct", label: t("Crecimiento anual (%)"), type: "number" },
      { key: "net_debt_eur", label: t("Deuda neta (€)"), type: "number" },
    ],
  },
  {
    id: 3,
    name: t("Cualificación"),
    desc: t("Aspectos clave que valoran los searchers."),
    fields: [
      { key: "recurrence", label: t("Recurrencia de ingresos"), type: "select", options: ["Alta (>70%)", "Media (30-70%)", "Baja (<30%)"] },
      { key: "customer_concentration", label: t("Concentración top-5 clientes (%)"), type: "number" },
      { key: "competitive_moat", label: t("Ventaja competitiva"), type: "textarea" },
      { key: "management_continuity", label: t("Continuidad del equipo directivo"), type: "select", options: ["Sí", "Parcial", "No"] },
      { key: "transferability", label: t("Transferibilidad del negocio"), type: "select", options: ["Alta", "Media", "Baja"] },
    ],
  },
  {
    id: 4,
    name: t("Teaser"),
    desc: t("Documento ciego para captar interés."),
    fields: [
      { key: "products_services", label: t("Productos / Servicios"), type: "textarea" },
      { key: "key_advantages", label: t("Ventajas clave"), type: "textarea" },
      { key: "growth_drivers", label: t("Drivers de crecimiento"), type: "textarea" },
      { key: "asking_price_range", label: t("Rango de valoración esperada"), type: "text", placeholder: t("p. ej. €5M - €8M") },
    ],
  },
  {
    id: 5,
    name: t("Information Memorandum"),
    desc: t("Documento dinámico generado con IA. Solo visible para searchers e inversores con NDA aprobado por ti."),
    fields: [],
  },
  {
    id: 6,
    name: t("Información para Análisis"),
    desc: t("Data Room de Pre-Due Diligence: las preguntas de searchers e inversores con NDA aprobado se clasifican por sección (Legal, Financiero, Fiscal, Laboral, Operacional, Otros) y se responden en el Data Room, donde también publicas la documentación de análisis. Todo es visible para los compradores con NDA aprobado, sin revelar quién preguntó."),
    fields: [],
  },
  {
    id: 7,
    name: t("Oferta Indicativa (LOI)"),
    desc: t("Se activa cuando un searcher, tras revisar tu IM, decide avanzar con una oferta formal. Define aquí la estructura de la operación y registra la carta de intenciones recibida."),
    locked_hint: t("Se desbloquea automáticamente cuando un searcher con NDA aprobado presenta una oferta (IOI/LOI) desde su pipeline."),
    fields: [
      { key: "investment_type", label: t("Tipo de inversión"), type: "select", options: ["equity", "debt", "mixed"] },
      { key: "deal_structure", label: t("Estructura propuesta"), type: "textarea" },
      { key: "equity_offered_pct", label: t("Equity ofrecido (%)"), type: "number" },
      { key: "investment_ask_eur", label: t("Capital buscado (€)"), type: "number" },
      { key: "expected_irr_pct", label: t("TIR objetivo (%)"), type: "number" },
      { key: "timeline", label: t("Timeline del proceso"), type: "textarea" },
      { key: "loi_offer_eur", label: t("Precio ofertado en la LOI (€)"), type: "number" },
      { key: "loi_exclusivity_days", label: t("Exclusividad solicitada (días)"), type: "number" },
      { key: "loi_received_at", label: t("Fecha de recepción de la LOI"), type: "date" },
      { key: "loi_status", label: t("Estado de la negociación"), type: "select", options: ["Recibida", "En negociación", "Aceptada", "Rechazada"] },
    ],
  },
  {
    id: 8,
    name: t("Due Diligence"),
    desc: t("Auditoría profunda del negocio: financiera, legal, fiscal y operativa."),
    fields: [
      { key: "dd_status", label: t("Estado de la Due Diligence"), type: "select", options: ["No iniciada", "En curso", "Completada con hallazgos", "Completada sin hallazgos"] },
      { key: "dd_start_date", label: t("Fecha de inicio"), type: "date" },
      { key: "dd_end_date", label: t("Fecha prevista de fin"), type: "date" },
      { key: "dd_advisors", label: t("Asesores que intervienen"), type: "text", placeholder: t("p. ej. auditora, despacho legal") },
      { key: "dd_notes", label: t("Hallazgos y notas"), type: "textarea" },
    ],
  },
  {
    id: 9,
    name: t("Revisión de Contratos"),
    desc: t("Redacción y validación legal de los acuerdos finales (SPA, pactos de socios, garantías)."),
    fields: [
      { key: "spa_status", label: t("Estado del contrato de compraventa (SPA)"), type: "select", options: ["Pendiente de borrador", "Borrador recibido", "En negociación", "Acordado"] },
      { key: "legal_counsel", label: t("Asesor legal del vendedor"), type: "text" },
      { key: "key_terms", label: t("Términos clave pactados"), type: "textarea", placeholder: t("Precio final, earn-out, garantías, no competencia…") },
      { key: "contracts_notes", label: t("Puntos abiertos"), type: "textarea" },
    ],
  },
  {
    id: 10,
    name: t("Cierre de la Operación"),
    desc: t("Firma y transferencia de la propiedad."),
    fields: [
      { key: "closing_status", label: t("Estado del cierre"), type: "select", options: ["Fecha fijada", "Firmado", "Transferencia completada"] },
      { key: "signing_date", label: t("Fecha de firma"), type: "date" },
      { key: "final_price_eur", label: t("Precio final de la operación (€)"), type: "number" },
      { key: "closing_notes", label: t("Notas de cierre"), type: "textarea" },
    ],
  },
];
